import Information16 from '@carbon/icons-react/es/information/16';
import { FormInstance, Tooltip, Typography } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import styled from 'styled-components';
import { User, UserStatus, UserType } from 'api/models';
import { PartnerCompany } from 'api/partner/types';
import { Avatar } from 'components/atoms/avatar';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { error } from 'components/atoms/modal';
import { Option, Select } from 'components/atoms/select';
import {
  AvatarOption,
  ListItem,
  ListItemMeta,
} from 'components/atoms/select/custom-components';
import { VirtualizedMenuList } from 'components/molecules/assignee-picker/virtualised-list';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { usePlaceholderValueHelpers } from 'components/molecules/project-details/header/placeholder-resolution/placeholder-value-helper';
import { translationKeyForAccessLevelLabel } from 'components/organisms/module-settings/partner/utils';
import { removeInsignificantTrailingZero } from 'components/organisms/resource-management/utils/dates';
import { TemplateDueDateResponse } from 'data/templates/api';
import { useTranslate } from 'hooks/use-translation';
import { useUsersQuery } from 'queries/members';
import { userOption } from 'utils/project-fields/construct-user-option';
import { NewUserOption, UserOption } from 'utils/project-fields/types';
import { getTranslationKeyForTeamHint } from './project-info-step-utils';
import {
  Col,
  PlaceholderLabel,
  PlaceholderMeta,
  PlaceholderTaskCount,
  Row,
  Label,
  PlaceholderSectionHeading,
} from './styled';
import { ProjectData, ProjectPlaceholder } from './types';

const NoMarginFormItem = styled(Form.Item)`
  margin-bottom: 0px;
  padding-bottom: var(--spacing-03) !important;
`;
const FormItem = styled(NoMarginFormItem)`
  .ant-form-item-control-input {
    align-items: flex-start;
  }
`;
const PlaceholderSection = styled.div`
  margin: var(--spacing-06) 0;
`;
const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

const CustomOption = (props) => {
  const { data, isSelected, isFocused } = props;

  return data.extra.type === 'company' ? (
    <components.Option {...props}>
      <ListItem>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={
            <Avatar
              name={data.label}
              src={data.imageUrl}
              companyLogoUrl={data.logoUrl}
            />
          }
          title={
            <TitleWrapper>
              {data.label}
              <Tooltip
                title={
                  <FormattedMessage id="projects.create.team_details.placeholder.partner.option.hint" />
                }
              >
                <Information16 />
              </Tooltip>
            </TitleWrapper>
          }
        />
      </ListItem>
    </components.Option>
  ) : (
    <AvatarOption {...props} />
  );
};

export interface VendorTeamStepFormValues {
  projectOwner?: UserOption | null;
  customerChampion?: UserOption | null;
  internalUsers?: UserOption[] | null;
  externalUsers?: UserOption[] | null;
  internalPlaceholders?: ProjectPlaceholder[];
  assignToProjectOwner?: boolean;
}

interface VendorTeamStepProps {
  form: FormInstance<VendorTeamStepFormValues>;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  onValuesChange: (values: any, allValues: unknown) => void;
  projectData: ProjectData;
  internalPlaceholders: ProjectPlaceholder[];
  partnerCompanies: PartnerCompany[];
  templateDetails?: TemplateDueDateResponse;
}

export const newUserOption = (input: string): NewUserOption => {
  return {
    value: input,
    label: input,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __isNew__: true,
  };
};

export const VendorTeamStep: FC<VendorTeamStepProps> = ({
  form,
  onValuesChange,
  projectData,
  internalPlaceholders,
  templateDetails,
}) => {
  const translate = useTranslate();
  const { data: users = [] } = useUsersQuery();
  const activeOrInvitedNativeUser = (user: User) =>
    user.userType === UserType.Native &&
    [UserStatus.Active, UserStatus.Invited].includes(user.userStatus);
  const initialValues: VendorTeamStepFormValues = {
    projectOwner: projectData.projectOwner,
  };

  if (
    form.getFieldValue('internalPlaceholders') == null ||
    form.getFieldValue('internalPlaceholders').length === 0
  ) {
    form.setFieldsValue({
      internalPlaceholders,
    });
  }
  const {
    getResponsibleCompanyForOption,
    doesCompanyHaveAcccesForPrivateTasks,
  } = usePlaceholderValueHelpers();
  const onValueChangeForPlaceholder = useCallback(
    (placeholder: ProjectPlaceholder, selected: Option) => {
      const placeholders = form.getFieldValue('internalPlaceholders');

      if ((placeholder.privateTaskCount ?? 0) > 0) {
        const responsibleCompany = getResponsibleCompanyForOption(selected);

        if (responsibleCompany != null) {
          const access =
            doesCompanyHaveAcccesForPrivateTasks(responsibleCompany);

          if (!access.hasAccess) {
            const title = translate(
              'projects.placeholder_resolutions.cannot_assigned.private_tasks.title',
            );
            const content = translate(
              'projects.placeholder_resolutions.cannot_assigned.private_tasks.description',
              {
                placeholderName: placeholder.teamName,
                companyName: selected.label,
                accessLevel:
                  access.permissionSetName != null
                    ? translate(
                        translationKeyForAccessLevelLabel(
                          access.permissionSetName,
                        ),
                      )
                    : '',
                isUser: selected.extra?.type === 'user',
                numberOfTasks: placeholder.privateTaskCount ?? 0,
                strong: (chunks) => <strong>{chunks}</strong>,
              },
            );

            error({
              title,
              content,
            });
            form.setFieldsValue({
              internalPlaceholders: placeholders.map((x) => {
                return x.teamId === placeholder.teamId
                  ? {
                      ...x,
                      user: null,
                    }
                  : x;
              }),
            });
          }
        }
      }
    },
    [
      doesCompanyHaveAcccesForPrivateTasks,
      form,
      getResponsibleCompanyForOption,
      translate,
    ],
  );

  return (
    <Form
      layout="vertical"
      form={form}
      name="vendorTeamStepForm"
      onValuesChange={onValuesChange}
      initialValues={initialValues}
    >
      <Row>
        <Col xs={8}>
          <div>
            <FormattedMessage id="projects.list.project_owner" />
          </div>
        </Col>
        <Col>
          <NoMarginFormItem name="projectOwner">
            <Select
              type="user"
              options={(users ?? [])
                .filter(
                  (u) =>
                    !(projectData.internalUsers || [])
                      .map((user) => user.value)
                      .includes(u.userId),
                )
                .filter(activeOrInvitedNativeUser)
                .map(userOption)}
            />
          </NoMarginFormItem>
        </Col>
      </Row>
      <Row>
        <Col offset={8}>
          <FormItem name="assignToProjectOwner" valuePropName="checked">
            <Checkbox>
              <FormattedMessage id="projects.create.assign.to.project.owner.checkbox.label" />
            </Checkbox>
          </FormItem>
        </Col>
      </Row>
      {internalPlaceholders.length > 0 ? (
        <PlaceholderSection>
          <PlaceholderSectionHeading>
            <span>
              <FormattedMessage id="projects.create.team_details.internal_teams.placeholder.title" />
            </span>
          </PlaceholderSectionHeading>
          <Form.List name="internalPlaceholders">
            {(fields) => (
              <>
                {fields.map((field) => (
                  <Row>
                    <Col xs={8}>
                      <PlaceholderLabel>
                        <span>
                          <PlaceholderAvatar
                            isCustomerRole={false}
                            name={internalPlaceholders[field.key]?.teamName}
                          />
                        </span>
                        <PlaceholderMeta>
                          <span>
                            {internalPlaceholders[field.key]?.teamName}
                          </span>
                          {internalPlaceholders[field.key].taskCount ||
                          internalPlaceholders[field.key]?.totalAllocations ? (
                            <PlaceholderTaskCount>
                              <FormattedMessage
                                id={`projects.create.team_details.placeholder.${getTranslationKeyForTeamHint(
                                  internalPlaceholders[field.key],
                                )}.label`}
                                values={{
                                  taskCount:
                                    internalPlaceholders[field.key]?.taskCount,
                                  totalAllocatedHours:
                                    removeInsignificantTrailingZero(
                                      (internalPlaceholders[field.key]
                                        ?.totalAllocations ?? 0) / 60,
                                    ),
                                }}
                              />
                            </PlaceholderTaskCount>
                          ) : null}
                        </PlaceholderMeta>
                      </PlaceholderLabel>
                    </Col>
                    <Col>
                      <Form.Item {...field} name={[field.name, 'user']}>
                        <Select
                          type="user"
                          isClearable
                          noOptionsMessage={({ inputValue }) => {
                            const input = inputValue
                              ? inputValue.trim()
                              : undefined;

                            if (isEmpty(users) && !input) {
                              return <FormattedMessage id="no.options" />;
                            }

                            return (
                              <FormattedMessage
                                id="no.matches.found"
                                values={{
                                  b: (chunks) => (
                                    <Typography.Text strong>
                                      {input ?? chunks}
                                    </Typography.Text>
                                  ),
                                }}
                              />
                            );
                          }}
                          placeholder={
                            <FormattedMessage id="projects.create.team_details.placeholders.input.placeholder" />
                          }
                          grouped
                          onChange={(selected) =>
                            onValueChangeForPlaceholder(
                              internalPlaceholders[field.key],
                              selected,
                            )
                          }
                          menuPortalTarget={document.body}
                          menuPlacement="auto"
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 'var(--zindex-popover-low)',
                            }),
                          }}
                          components={{
                            MenuList: VirtualizedMenuList,
                            Option: CustomOption,
                          }}
                          startDate={projectData.startDate}
                          dueDate={
                            projectData.dueDate
                              ? projectData.dueDate
                              : templateDetails?.dueDate
                              ? moment(templateDetails?.dueDate)
                              : undefined
                          }
                          options={[
                            {
                              id: 'my_team',
                              label: 'My team',
                              options: (users ?? [])
                                .filter(activeOrInvitedNativeUser)
                                .map(userOption)
                                .sort((u) =>
                                  u.roleId ===
                                  internalPlaceholders[field.key]?.roleId
                                    ? -1
                                    : 1,
                                ),
                            },
                            ...(projectData.partners ?? []).map((partner) => ({
                              id: `partner-${partner.value}`,
                              label: partner.label,
                              options: [
                                {
                                  ...partner,
                                  value: `partner-${partner.value}`,
                                  extra: { id: partner.value, type: 'company' },
                                } as UserOption,
                              ].concat(
                                (users ?? [])
                                  .filter(
                                    ({ userStatus, company: { companyId } }) =>
                                      companyId === partner.value &&
                                      userStatus !== UserStatus.Inactive,
                                  )
                                  .map(userOption)
                                  .sort((u) =>
                                    u.roleId ===
                                    internalPlaceholders[field.key]?.roleId
                                      ? -1
                                      : 1,
                                  ),
                              ),
                            })),
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Form.List>
        </PlaceholderSection>
      ) : null}
      <Row>
        <Col xs={8}>
          <Label>
            <FormattedMessage id="projects.create.team_details.invite_team" />
          </Label>
        </Col>
        <Col>
          <NoMarginFormItem name="internalUsers">
            <Select
              type="user"
              isMulti
              isClearable
              closeMenuOnSelect={false}
              startDate={projectData.startDate}
              dueDate={
                projectData.dueDate
                  ? projectData.dueDate
                  : templateDetails?.dueDate
                  ? moment(templateDetails?.dueDate)
                  : undefined
              }
              noOptionsMessage={({ inputValue }) => {
                const input = inputValue ? inputValue.trim() : undefined;

                if (isEmpty(users) && !input) {
                  return <FormattedMessage id="no.options" />;
                }

                return (
                  <FormattedMessage
                    id="no.matches.found"
                    values={{
                      b: (chunks) => (
                        <Typography.Text strong>
                          {input ?? chunks}
                        </Typography.Text>
                      ),
                    }}
                  />
                );
              }}
              options={(users ?? [])
                .filter((u) => u.userId !== projectData.projectOwner?.value)
                .filter(activeOrInvitedNativeUser)
                .map(userOption)}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 'var(--zindex-popover-low)',
                }),
              }}
            />
          </NoMarginFormItem>
        </Col>
      </Row>
    </Form>
  );
};
